import React, { useEffect, useMemo, useRef, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebaseConfig";
import * as XLSX from "xlsx";
import { getAuth } from "firebase/auth";

const formatDate = (date) => {
  if (!date) return "Tarih yok";
  if (date.seconds) {
    return new Date(date.seconds * 1000).toLocaleDateString("tr-TR");
  }
  try {
    return new Date(date).toLocaleDateString("tr-TR");
  } catch {
    return "Geçersiz Tarih";
  }
};

const calculateDaysAgo = (date) => {
  if (!date?.seconds) return "";
  const now = new Date();
  const created = new Date(date.seconds * 1000);
  const diffTime = Math.abs(now - created);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return `(${diffDays} gün)`;
};

// TR uyumlu slug
const toSlugTR = (str = "") => {
  const map = { 'Ç':'c','Ö':'o','Ş':'s','İ':'i','I':'i','Ğ':'g','Ü':'u','ç':'c','ö':'o','ş':'s','ı':'i','ğ':'g','ü':'u' };
  const replaced = (str || "").replace(/[ÇÖŞİIĞÜçöşığü]/g, ch => map[ch] || ch);
  return replaced
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

/* ====== Küçük boy filtre stili ====== */
const fieldStyle = {
  width: "100%",
  padding: "6px 10px",
  borderRadius: 6,
  border: "1px solid #cbd5e1",
  fontSize: 13,
  boxSizing: "border-box",
};
const chipStyle = {
  display: "inline-block",
  background: "#f1f5f9",
  border: "1px solid #e2e8f0",
  borderRadius: 999,
  padding: "2px 10px",
  fontSize: 12,
  color: "#0f172a",
};

/* ================== Plaka Autocomplete ================== */
const AutocompletePlate = ({ value, onCommit, options, placeholder = "Tüm Plakalar" }) => {
  const [input, setInput] = useState(value || "");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => { setInput(value || ""); }, [value]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        onCommit((input || "").trim());
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [input, onCommit]);

  const filtered = useMemo(() => {
    const s = (input || "").toLowerCase();
    if (!s) return options.slice(0, 50);
    return options.filter(p => (p || "").toLowerCase().includes(s)).slice(0, 50);
  }, [input, options]);

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      onCommit((input || "").trim());
      setOpen(false);
    }
  };

  return (
    <div style={{ position: "relative" }} ref={wrapRef}>
      <input
        value={input}
        onChange={(e) => { setInput(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        style={fieldStyle}
      />
      {open && filtered.length > 0 && (
        <ul style={{
          position: "absolute", zIndex: 50, listStyle: "none", margin: "4px 0 0 0", padding: 0,
          width: "100%", maxHeight: 220, overflowY: "auto", background: "#fff",
          border: "1px solid #cbd5e1", borderRadius: 6, boxShadow: "0 6px 16px rgba(0,0,0,0.08)"
        }}>
          {filtered.map((opt, i) => (
            <li
              key={`${opt}-${i}`}
              onMouseDown={(e) => { e.preventDefault(); onCommit(opt); setInput(opt); setOpen(false); }}
              style={{ padding: "8px 10px", cursor: "pointer" }}
            >
              {opt}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
/* ======================================================= */

const ViewIssues = () => {
  const [issues, setIssues] = useState([]);
  const [filteredIssues, setFilteredIssues] = useState([]);
  const [selectedTesis, setSelectedTesis] = useState("");
  const [selectedPlate, setSelectedPlate] = useState("");
  const [terminMap, setTerminMap] = useState({});   // termin kategorileri
  const [titleMap, setTitleMap] = useState({});     // başlık→kategori (id=slug)
  const [terminFilter, setTerminFilter] = useState("all"); // all | not_overdue | overdue
  const [lastResolvedIdx, setLastResolvedIdx] = useState({}); // 🔥 PLATE|slug(issue) → last resolvedAt (seconds)
  const auth = getAuth();

  // Açık (çözülmemiş) issues
  useEffect(() => {
    const qRef = query(collection(db, "issues"), where("resolved", "==", false));
    const unsubscribe = onSnapshot(qRef, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setIssues(data);
    });
    return () => unsubscribe();
  }, []);

  // ÇÖZÜLEN issues → indeks (plate+title)
  useEffect(() => {
    const qRef = query(collection(db, "issues"), where("resolved", "==", true));
    const unsub = onSnapshot(qRef, (snap) => {
      const idx = {};
      snap.forEach((d) => {
        const v = d.data();
        const plate = (v.plate || "").toUpperCase();
        const slug = toSlugTR((v.issue || "").trim());
        if (!plate || !slug) return;
        const sec = v?.resolvedAt?.seconds || v?.date?.seconds || null;
        if (!sec) return;
        const key = `${plate}|${slug}`;
        if (!idx[key] || sec > idx[key]) {
          idx[key] = sec; // en son
        }
      });
      setLastResolvedIdx(idx);
    });
    return () => unsub();
  }, []);

  // termin koleksiyonu
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "termin"), (snap) => {
      const m = {};
      snap.forEach(d => { m[d.id] = { id: d.id, ...d.data() }; });
      setTerminMap(m);
    });
    return () => unsub();
  }, []);

  // başlık eşlemesi
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "termin_title_map"), (snap) => {
      const m = {};
      snap.forEach(d => { m[d.id] = { id: d.id, ...d.data() }; }); // id=slug
      setTitleMap(m);
    });
    return () => unsub();
  }, []);

  // Bir issue için kategori (termin gün) bul
  const getCategoryForIssue = (it) => {
    if (it.terminCategoryId && terminMap[it.terminCategoryId]) return terminMap[it.terminCategoryId];
    const slug = toSlugTR((it.issue || "").trim());
    const mapDoc = slug ? titleMap[slug] : null;
    if (mapDoc && mapDoc.categoryId && terminMap[mapDoc.categoryId]) {
      return terminMap[mapDoc.categoryId];
    }
    return null;
  };

  // Filtreleme
  useEffect(() => {
    let filtered = issues;

    if (selectedTesis) {
      filtered = filtered.filter(item => item.tesis === selectedTesis);
    }
    if (selectedPlate) {
      const val = selectedPlate.toLowerCase();
      filtered = filtered.filter(item => (item.plate || "").toLowerCase().includes(val));
    }

    if (terminFilter !== "all") {
      filtered = filtered.filter((item) => {
        const cat = getCategoryForIssue(item);
        const terminDays = typeof cat?.days === "number" ? cat.days : null;
        if (terminDays == null) return false;

        const now = new Date();
        const created = item.date?.seconds ? new Date(item.date.seconds * 1000) : null;
        if (!created) return false;

        const passed = Math.ceil((now - created) / (1000 * 60 * 60 * 24));
        const remaining = terminDays - passed;
        const overdue = remaining < 0;

        return terminFilter === "overdue" ? overdue : !overdue;
      });
    }

    setFilteredIssues(filtered);
  }, [issues, selectedTesis, selectedPlate, terminFilter, terminMap, titleMap]);

  const grouped = useMemo(() => {
    const g = {};
    filteredIssues.forEach((item) => {
      const tesisKey = item.tesis || "Belirtilmemiş";
      if (!g[tesisKey]) g[tesisKey] = {};
      if (!g[tesisKey][item.plate]) g[tesisKey][item.plate] = [];
      g[tesisKey][item.plate].push(item);
    });
    return g;
  }, [filteredIssues]);

  const uniqueTesis = [...new Set(issues.map(i => i.tesis).filter(Boolean))];
  const uniquePlates = [...new Set(issues.map(i => i.plate).filter(Boolean))];

  const exportToExcel = () => {
    const exportData = filteredIssues.map(item => {
      const cat = getCategoryForIssue(item);
      const terminDays = typeof cat?.days === "number" ? cat.days : null;
      let kalan = "-";
      if (terminDays != null && item.date?.seconds) {
        const now = new Date();
        const created = new Date(item.date.seconds * 1000);
        const passed = Math.ceil((now - created) / (1000 * 60 * 60 * 24));
        const remaining = terminDays - passed;
        kalan = remaining > 0 ? `${remaining} gün kaldı` : remaining === 0 ? "bugün" : `${Math.abs(remaining)} gün geçti`;
      }

      return {
        Tesis: item.tesis,
        Plaka: item.plate,
        Eksiklik: item.issue,
        Tarih: formatDate(item.date),
        Gun: calculateDaysAgo(item.date),
        "Termin (gün)": terminDays ?? "-",
        "Termine Kalan": kalan,
        Ekleyen: item.createdBy || "-",
        Cözen: item.resolvedBy || "-"
      };
    });
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Eksiklikler");
    XLSX.writeFile(wb, "eksiklikler.xlsx");
  };

  // Tesis bazında tüm açık kayıt sayısı (filtre bağımsız)
  const tesisCount = issues.reduce((acc, item) => {
    const key = item.tesis || "Belirtilmemiş";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  // Bir kaydın termininin geçip geçmediğini hesapla
  const isOverdue = (item) => {
    const cat = getCategoryForIssue(item);
    const terminDays = typeof cat?.days === "number" ? cat.days : null;
    if (terminDays == null || !item?.date?.seconds) return null;
    const now = new Date();
    const created = new Date(item.date.seconds * 1000);
    const passed = Math.ceil((now - created) / (1000 * 60 * 60 * 24));
    const remaining = terminDays - passed;
    return remaining < 0;
  };

  // Tesis bazında "termini geçen" adet
  const overdueCountByTesis = useMemo(() => {
    const acc = {};
    issues.forEach((it) => {
      const od = isOverdue(it);
      if (od === true) {
        const key = it.tesis || "Belirtilmemiş";
        acc[key] = (acc[key] || 0) + 1;
      }
    });
    return acc;
  }, [issues, terminMap, titleMap]);

  // Yardımcı: kalan süre metni & renklendirme (tablo)
  const calcRemainingInfo = (item) => {
    const cat = getCategoryForIssue(item);
    const terminDays = typeof cat?.days === "number" ? cat.days : null;
    if (terminDays == null || !item?.date?.seconds) return { label: "-", overdue: null };

    const now = new Date();
    const created = new Date(item.date.seconds * 1000);
    const passed = Math.ceil((now - created) / (1000 * 60 * 60 * 24));
    const remaining = terminDays - passed;

    let label = "-";
    if (remaining > 0) label = `${remaining} gün kaldı`;
    else if (remaining === 0) label = "bugün";
    else label = `${Math.abs(remaining)} gün geçti`;

    return { label, overdue: remaining < 0 };
  };

  // 🔥 Yardımcı: aynı plaka + başlık için SON çözümden bu yana kaç gün?
  const getLastResolvedLabel = (plate, issue, createdSeconds) => {
    const key = `${(plate || "").toUpperCase()}|${toSlugTR((issue || "").trim())}`;
    const sec = lastResolvedIdx[key];
    if (!sec) return null;
    // Sadece BU kayıttan önceki çözümleri dikkate al (daha mantıklı)
    if (createdSeconds && sec >= createdSeconds) return null;

    const nowMs = Date.now();
    const days = Math.ceil((nowMs - sec * 1000) / (1000 * 60 * 60 * 24));
    return `Son çözüm: ${days} gün önce`;
  };

  return (
    <div style={{ padding: 20 }}>
      <h2 style={{ textAlign: "center", marginBottom: 12 }}>Araç Eksiklikleri</h2>

      {/* Filtreler */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 10,
          marginBottom: 8,
          alignItems: "center",
        }}
      >
        <select value={selectedTesis} onChange={(e) => setSelectedTesis(e.target.value)} style={fieldStyle}>
          <option value="">Tüm Tesisler</option>
          {Object.keys(tesisCount).map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>

        <AutocompletePlate
          value={selectedPlate}
          onCommit={setSelectedPlate}
          options={uniquePlates}
          placeholder="Tüm Plakalar (yaz ve Enter)"
        />

        <select value={terminFilter} onChange={(e) => setTerminFilter(e.target.value)} style={fieldStyle}>
          <option value="all">Termin: Tümü</option>
          <option value="not_overdue">Termin: Geçmeyenler</option>
          <option value="overdue">Termin: Geçenler</option>
        </select>

        <button
          onClick={exportToExcel}
          style={{ padding: "8px 12px", border: "none", borderRadius: 6, cursor: "pointer", background: "#3b82f6", color: "#fff" }}
        >
          Excel'e Aktar
        </button>
      </div>

      {/* Filtrelenen toplam adet */}
      <div style={{ marginBottom: 10 }}>
        <span style={chipStyle}>Filtrelenen: <b>{filteredIssues.length}</b></span>
      </div>

      {/* Özet KUTULAR */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 10,
          marginBottom: 16,
        }}
      >
        {Object.entries(tesisCount).map(([tesis, count]) => (
          <div
            key={tesis}
            style={{
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 10,
              padding: "10px 12px",
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 6 }}>{tesis}: {count}</div>
            <div style={{ fontSize: 12, color: "#475569" }}>
              Termin Geçen: <b>{overdueCountByTesis[tesis] ?? 0}</b>
            </div>
          </div>
        ))}
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: "700px", fontFamily: "Arial" }}>
          <thead style={{ backgroundColor: "#1e293b", color: "black" }}>
            <tr>
              <th style={{ border: "1px solid #ccc", padding: "10px" }}>Tesis</th>
              <th style={{ border: "1px solid #ccc", padding: "10px" }}>Plaka</th>
              <th style={{ border: "1px solid #ccc", padding: "10px" }}>Eksiklik</th>
              <th style={{ border: "1px solid #ccc", padding: "10px" }}>Terminin Bitmesine Kalan</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(grouped).map(([tesis, plakaGroup]) => {
              const tesisRowSpan = Object.values(plakaGroup).reduce((acc, items) => acc + items.length, 0);
              let tesisPrinted = false;
              return Object.entries(plakaGroup).map(([plate, itemList]) => {
                return itemList.map((item, itemIdx) => {
                  const { label: kalanLabel, overdue } = calcRemainingInfo(item);

                  const issueCellStyle = {
                    border: "1px solid #ccc",
                    padding: "10px",
                    verticalAlign: "top",
                    backgroundColor: overdue === null ? "transparent" : (overdue ? "#fee2e2" : "#dcfce7"),
                    color: overdue ? "#991b1b" : "#0f172a"
                  };
                  const kalanCellStyle = {
                    border: "1px solid #ccc",
                    padding: "10px",
                    verticalAlign: "top",
                    fontWeight: overdue ? "bold" : "normal",
                    backgroundColor: overdue === null ? "transparent" : (overdue ? "#fee2e2" : "#dcfce7"),
                    color: overdue ? "#991b1b" : "#065f46"
                  };

                  const row = [];

                  if (!tesisPrinted) {
                    row.push(
                      <td key={`tesis-${tesis}`} rowSpan={tesisRowSpan} style={{ border: "1px solid #ccc", padding: "10px", fontWeight: "bold", backgroundColor: "#f1f5f9", verticalAlign: "top" }}>{tesis}</td>
                    );
                    tesisPrinted = true;
                  }

                  if (itemIdx === 0) {
                    row.push(
                      <td key={`plate-${plate}`} rowSpan={itemList.length} style={{ border: "1px solid #ccc", padding: "10px", fontWeight: "bold", backgroundColor: "#e2e8f0", verticalAlign: "top" }}>{plate}</td>
                    );
                  }

                  // 🔥 Yeni satır: Son çözüm bilgisi (varsa)
                  const createdSec = item?.date?.seconds || null;
                  const lastResolvedLabel = getLastResolvedLabel(item.plate, item.issue, createdSec);

                  row.push(
                    <td key={`issue-${item.id}`} style={issueCellStyle}>
                      <div style={{ fontWeight: "bold" }}>
                        {item.issue} <span style={{ fontSize: "0.8em", color: "#666" }}>{calculateDaysAgo(item.date)}</span>
                      </div>

                      {lastResolvedLabel && (
                        <div style={{ fontSize: "0.8em", color: "#475569", marginTop: 4 }}>
                          {lastResolvedLabel}
                        </div>
                      )}

                      <div style={{ fontSize: "0.75em", color: "#555", marginTop: 2 }}>
                        Ekleyen: {item.createdBy || "-"}
                      </div>

                      {item.resolvedBy && (
                        <div style={{ fontSize: "0.75em", color: "green" }}>Çözen: {item.resolvedBy}</div>
                      )}
                    </td>
                  );

                  row.push(
                    <td key={`kalan-${item.id}`} style={kalanCellStyle}>
                      {kalanLabel}
                    </td>
                  );

                  return <tr key={item.id}>{row}</tr>;
                });
              });
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ViewIssues;
